/**
 * Pass Offer Wizard — 7 steps.
 * Used for both create (no id) and edit (with :offerId param).
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, ArrowRight, Check, Sparkles, Gift, DollarSign,
  Users, Calendar, Save, Layers, ChefHat, Ship, Map, Music, Hotel, Sun, Gem, Car,
  Image as ImageIcon, Upload, X, Camera,
} from 'lucide-react';
import { DashboardLayout } from '../../../layouts/DashboardLayout';
import { passBusiness } from '../../../services/passApi';
import { uploadProductImage } from '../../../services/api';

const CATEGORIES = [
  { value: 'restaurant', label: 'Restaurante', Icon: ChefHat },
  { value: 'yacht', label: 'Yate', Icon: Ship },
  { value: 'tour', label: 'Tour', Icon: Map },
  { value: 'nightlife', label: 'Antro / Nightlife', Icon: Music },
  { value: 'hotel_daypass', label: 'Hotel Day Pass', Icon: Hotel },
  { value: 'beach_club', label: 'Beach Club', Icon: Sun },
  { value: 'jewelry', label: 'Joyería', Icon: Gem },
  { value: 'vip_transport', label: 'Transporte VIP', Icon: Car },
];

const MODES = [
  { value: 'free_claim',       label: 'Promo gratis',   hint: 'Cliente reclama sin pagar.', paid: false },
  { value: 'reservation_only', label: 'Solo reservar',  hint: 'Sin pago online — apartar lugar.', paid: false },
  { value: 'deposit',          label: 'Apartado',       hint: 'Cliente paga un depósito online; resto en el negocio.', paid: true },
  { value: 'full_payment',     label: 'Pago completo',  hint: 'Cliente paga todo online.', paid: true },
  { value: 'cover_payment',    label: 'Cover',          hint: 'Cliente paga un cover/entrada online.', paid: true },
  { value: 'manual_lead',      label: 'Contacto manual', hint: 'Solo registra interés; sin reserva ni pago.', paid: false },
];

const COMMISSION_TYPES = [
  { value: 'fixed_per_group',       label: 'Fijo por grupo / pareja' },
  { value: 'fixed_per_person',      label: 'Fijo por persona' },
  { value: 'fixed_per_reservation', label: 'Fijo por reserva' },
  { value: 'percent_of_sale',       label: '% de la venta' },
];

const COMMISSION_TRIGGERS = [
  { value: 'benefit_delivered',    label: 'Cuando entregue el beneficio (default)' },
  { value: 'checked_in',           label: 'En el check-in' },
  { value: 'payment_completed',    label: 'Al completar el pago online' },
  { value: 'manual_approval',      label: 'Aprobación manual' },
];

const STEPS = ['Categoría', 'Modo', 'Beneficio', 'Precio', 'Comisión', 'Capacidad', 'Resumen'];

const EMPTY = {
  title: '',
  description: '',
  address: '',
  category: 'restaurant',
  mode: 'free_claim',
  customer_benefits: [{ type: 'free_drink', name: '', conditions: '' }],
  included_items: [],
  addons: [],
  pricing: { pricing_type: 'free', base_price: 0, pay_in_app: 0, pay_at_business: 0, currency: 'MXN' },
  affiliate_commission: {
    enabled: true, type: 'fixed_per_group', value: 150, group_size: 2,
    platform_cut_type: 'fixed', platform_cut_value: 30, trigger: 'benefit_delivered',
  },
  capacity: { enabled: true, real_capacity: 50, overbooking_enabled: false, overbooking_percent: 0, max_sellable_capacity: 50, capacity_unit: 'person', scope: 'total' },
  surge_rules: [],
  platform_fee: { type: null, value: 0, charged_to: 'business' },
  marketplace_visible: true,
  city: 'Puerto Vallarta',
  valid_days: 7,
  images: [],
};

export default function PassOfferForm() {
  const navigate = useNavigate();
  const { offerId } = useParams();
  const isEdit = !!offerId;

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    passBusiness.getOffer(offerId)
      .then((res) => {
        const o = res.data.offer;
        setForm({ ...EMPTY, ...o });
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'No se pudo cargar la oferta');
        navigate('/dashboard/pass');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function updateNested(group, key, value) {
    setForm((f) => ({ ...f, [group]: { ...f[group], [key]: value } }));
  }

  function applyTemplate(vertical) {
    passBusiness.templates().then((res) => {
      const tpl = (res.data.templates || []).find((t) => t.vertical === vertical);
      if (!tpl) return;
      const benefits = tpl.customer_benefits?.length ? tpl.customer_benefits : EMPTY.customer_benefits;
      setForm((f) => ({
        ...f,
        title: tpl.title || f.title,
        description: tpl.description || f.description,
        category: tpl.category || f.category,
        mode: tpl.mode || f.mode,
        customer_benefits: benefits,
        pricing: { ...EMPTY.pricing, ...(tpl.pricing || {}) },
        affiliate_commission: { ...EMPTY.affiliate_commission, ...(tpl.affiliate_commission || {}) },
        capacity: { ...EMPTY.capacity, ...(tpl.capacity || {}) },
        valid_days: tpl.valid_days ?? f.valid_days,
      }));
      toast.success('Plantilla cargada — revísala y publica');
    }).catch(() => toast.error('No se pudo cargar la plantilla'));
  }

  async function handleSubmit() {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        category: form.category,
        mode: form.mode,
        description: form.description || null,
        images: form.images || [],
        address: form.address || null,
        city: form.city || 'Puerto Vallarta',
        valid_days: Number(form.valid_days) || 7,
        marketplace_visible: !!form.marketplace_visible,
        surge_rules: (form.surge_rules || []).filter((r) => Number(r.value) > 0 && Number(r.increase_value) > 0),
        customer_benefits: (form.customer_benefits || []).filter((b) => b.name?.trim()),
        included_items: (form.included_items || []).filter((i) => i.name?.trim()),
        addons: (form.addons || []).filter((a) => a.id?.trim() && a.name?.trim()),
        pricing: {
          pricing_type: form.pricing.pricing_type,
          base_price: Number(form.pricing.base_price) || 0,
          pay_in_app: Number(form.pricing.pay_in_app) || 0,
          pay_at_business: Number(form.pricing.pay_at_business) || 0,
          currency: form.pricing.currency || 'MXN',
        },
        capacity: {
          enabled: !!form.capacity.enabled,
          real_capacity: Number(form.capacity.real_capacity) || 0,
          overbooking_enabled: !!form.capacity.overbooking_enabled,
          overbooking_percent: Number(form.capacity.overbooking_percent) || 0,
          max_sellable_capacity: Number(form.capacity.max_sellable_capacity) || Number(form.capacity.real_capacity) || 0,
          capacity_unit: form.capacity.capacity_unit || 'person',
          scope: form.capacity.scope || 'total',
        },
        affiliate_commission: {
          enabled: !!form.affiliate_commission.enabled,
          type: form.affiliate_commission.type,
          value: Number(form.affiliate_commission.value) || 0,
          group_size: Number(form.affiliate_commission.group_size) || 1,
          platform_cut_type: form.affiliate_commission.platform_cut_type || 'fixed',
          platform_cut_value: Number(form.affiliate_commission.platform_cut_value) || 0,
          trigger: form.affiliate_commission.trigger || 'benefit_delivered',
        },
        platform_fee: form.platform_fee || { type: null, value: 0, charged_to: 'business' },
      };
      if (isEdit) {
        await passBusiness.updateOffer(offerId, payload);
        toast.success('Oferta actualizada');
      } else {
        await passBusiness.createOffer(payload);
        toast.success('Oferta creada');
      }
      navigate('/dashboard/pass');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  }

  function canNext() {
    if (step === 0) return !!form.category;
    if (step === 1) return !!form.mode;
    if (step === 2) {
      const benefits = form.customer_benefits || [];
      return benefits.length === 0 || benefits.every((b) => b.name?.trim());
    }
    if (step === 5) return Number(form.capacity.real_capacity) >= 0;
    if (step === 6) return form.title?.trim();
    return true;
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="px-6 py-6 max-w-3xl">
        <button onClick={() => navigate('/dashboard/pass')} className="inline-flex items-center gap-1 text-sm text-slate-600 mb-3 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <h1 className="text-2xl font-bold text-slate-900 mb-1">
          {isEdit ? 'Editar oferta Pass' : 'Nueva oferta Pass'}
        </h1>
        <p className="text-sm text-slate-500 mb-5">Configuración guiada por pasos.</p>

        {/* Stepper */}
        <Stepper steps={STEPS} current={step} onStep={(i) => setStep(i)} />

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 mt-4 space-y-4">
          {step === 0 && <Step0 form={form} update={update} applyTemplate={applyTemplate} />}
          {step === 1 && <Step1 form={form} update={update} />}
          {step === 2 && <Step2 form={form} setForm={setForm} />}
          {step === 3 && <Step3 form={form} setForm={setForm} updateNested={updateNested} />}
          {step === 4 && <Step4 form={form} updateNested={updateNested} />}
          {step === 5 && <Step5 form={form} setForm={setForm} updateNested={updateNested} update={update} />}
          {step === 6 && <Step6 form={form} setForm={setForm} update={update} />}
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between gap-2 mt-4">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm disabled:opacity-40"
          >
            <ArrowLeft className="w-4 h-4" /> Anterior
          </button>
          {step < STEPS.length - 1 ? (
            <button
              onClick={() => canNext() && setStep((s) => s + 1)}
              disabled={!canNext()}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-40"
            >
              Siguiente <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={saving || !form.title?.trim()}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isEdit ? 'Guardar cambios' : 'Publicar oferta'}
            </button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stepper({ steps, current, onStep }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2">
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <button
            key={label}
            type="button"
            onClick={() => i <= current && onStep(i)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              active ? 'bg-slate-900 text-white'
                : done ? 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                : 'bg-slate-100 text-slate-500'
            }`}
          >
            {done ? <Check className="w-3 h-3" /> : <span className="w-4 h-4 rounded-full bg-white/30 text-[10px] flex items-center justify-center">{i + 1}</span>}
            {label}
          </button>
        );
      })}
    </div>
  );
}

// -------- Step components --------

function Step0({ form, update, applyTemplate }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-700 mb-3 inline-flex items-center gap-2">
        <Layers className="w-4 h-4 text-teal-600" /> Tipo de negocio / vertical
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {CATEGORIES.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => update('category', value)}
            className={`flex flex-col items-center gap-1 p-3 rounded-xl border text-xs font-semibold transition-colors ${
              form.category === value ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 hover:border-slate-300 text-slate-700'
            }`}
          >
            <Icon className="w-5 h-5" /> {label}
          </button>
        ))}
      </div>

      <div className="mt-5 p-3 rounded-lg bg-slate-50 border border-slate-200">
        <p className="text-xs font-semibold text-slate-700 mb-2 inline-flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5 text-orange-500" /> Plantilla pre-llena (opcional)
        </p>
        <p className="text-xs text-slate-500 mb-2">
          Carga valores típicos del vertical seleccionado para no empezar en blanco.
        </p>
        <button
          type="button"
          onClick={() => applyTemplate(form.category)}
          className="px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-white text-xs font-semibold text-slate-700"
        >
          Cargar plantilla {CATEGORIES.find((c) => c.value === form.category)?.label}
        </button>
      </div>
    </div>
  );
}

function Step1({ form, update }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-700 mb-3">Modo de oferta</h3>
      <div className="space-y-2">
        {MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => update('mode', m.value)}
            className={`w-full text-left p-3 rounded-xl border transition-colors flex items-start gap-3 ${
              form.mode === m.value ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${m.paid ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-600'}`}>
              {m.paid ? <DollarSign className="w-4 h-4" /> : <Gift className="w-4 h-4" />}
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm text-slate-900">{m.label}</p>
              <p className="text-xs text-slate-500">{m.hint}</p>
            </div>
            {form.mode === m.value && <Check className="w-4 h-4 text-teal-600 mt-1" />}
          </button>
        ))}
      </div>
    </div>
  );
}

function Step2({ form, setForm }) {
  const benefits = form.customer_benefits || [];
  const items = form.included_items || [];
  function setBenefit(i, key, value) {
    const arr = [...benefits];
    arr[i] = { ...arr[i], [key]: value };
    setForm((f) => ({ ...f, customer_benefits: arr }));
  }
  function addBenefit() {
    setForm((f) => ({ ...f, customer_benefits: [...(f.customer_benefits || []), { type: 'gift', name: '', conditions: '' }] }));
  }
  function removeBenefit(i) {
    setForm((f) => ({ ...f, customer_benefits: (f.customer_benefits || []).filter((_, idx) => idx !== i) }));
  }
  function setItem(i, key, value) {
    const arr = [...items];
    arr[i] = { ...arr[i], [key]: value };
    setForm((f) => ({ ...f, included_items: arr }));
  }
  function addItem() {
    setForm((f) => ({ ...f, included_items: [...(f.included_items || []), { type: 'benefit', name: '', description: '' }] }));
  }
  function removeItem(i) {
    setForm((f) => ({ ...f, included_items: (f.included_items || []).filter((_, idx) => idx !== i) }));
  }
  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-1">Beneficio para el cliente</h3>
        <p className="text-xs text-slate-500 mb-3">Lo que recibe al validar la promo. Puedes agregar varios.</p>
        <div className="space-y-2">
          {benefits.map((b, i) => (
            <div key={i} className="p-3 rounded-lg border border-slate-200 space-y-2">
              <input
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Nombre del beneficio (ej. 1 Margarita gratis)"
                value={b.name || ''}
                onChange={(e) => setBenefit(i, 'name', e.target.value)}
              />
              <input
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Condiciones (ej. Al consumir alimentos)"
                value={b.conditions || ''}
                onChange={(e) => setBenefit(i, 'conditions', e.target.value)}
              />
              {benefits.length > 1 && (
                <button type="button" onClick={() => removeBenefit(i)} className="text-xs text-red-600 hover:underline">
                  Quitar
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={addBenefit} className="text-sm text-teal-600 hover:underline">
            + Agregar otro beneficio
          </button>
        </div>
      </div>

      <div className="pt-4 border-t border-slate-200">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Items incluidos (opcional)</h4>
        <p className="text-xs text-slate-500 mb-3">
          Lo que ya viene incluido en el precio (cover, botella, mesa, transporte…). Se muestran en el ticket del cliente.
        </p>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="p-3 rounded-lg border border-slate-200 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <select
                value={it.type}
                onChange={(e) => setItem(i, 'type', e.target.value)}
                className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs"
              >
                {['benefit', 'cover', 'table', 'bottle', 'seat', 'food', 'drink', 'transport', 'room', 'vehicle', 'decoration'].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <input
                className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm sm:col-span-2"
                placeholder="Nombre (ej. Mesa para 4)"
                value={it.name || ''}
                onChange={(e) => setItem(i, 'name', e.target.value)}
              />
              <input
                className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs sm:col-span-3"
                placeholder="Descripción (opcional)"
                value={it.description || ''}
                onChange={(e) => setItem(i, 'description', e.target.value)}
              />
              <button type="button" onClick={() => removeItem(i)} className="text-xs text-red-600 hover:underline justify-self-start">
                Quitar
              </button>
            </div>
          ))}
          <button type="button" onClick={addItem} className="text-sm text-teal-600 hover:underline">
            + Agregar item incluido
          </button>
        </div>
      </div>
    </div>
  );
}

function Step3({ form, setForm, updateNested }) {
  const mode = form.mode;
  const requiresOnline = ['deposit', 'full_payment', 'cover_payment'].includes(mode);
  const addons = form.addons || [];

  function slugifyId(name) {
    return (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 32) || `addon_${Date.now()}`;
  }
  function setAddon(i, key, value) {
    setForm((f) => {
      const arr = [...(f.addons || [])];
      arr[i] = { ...arr[i], [key]: value };
      if (key === 'name' && (!arr[i].id || arr[i].id.startsWith('addon_'))) {
        arr[i].id = slugifyId(value);
      }
      return { ...f, addons: arr };
    });
  }
  function addAddon() {
    setForm((f) => ({
      ...f,
      addons: [...(f.addons || []), { id: '', name: '', price: 0, pay_mode: 'pay_in_app', max_quantity: 1 }],
    }));
  }
  function removeAddon(i) {
    setForm((f) => ({ ...f, addons: (f.addons || []).filter((_, idx) => idx !== i) }));
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-700 mb-3 inline-flex items-center gap-2">
        <DollarSign className="w-4 h-4 text-orange-500" /> Precio y pago
      </h3>

      <Field label="Tipo de precio">
        <select
          value={form.pricing.pricing_type}
          onChange={(e) => updateNested('pricing', 'pricing_type', e.target.value)}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="free">Gratis</option>
          <option value="package">Paquete</option>
          <option value="per_person">Por persona</option>
          <option value="deposit_only">Solo apartado</option>
          <option value="minimum_consumption">Consumo mínimo</option>
          <option value="manual">Manual</option>
        </select>
      </Field>

      <Field label="Precio total (MXN)">
        <input
          type="number" min={0}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          value={form.pricing.base_price}
          onChange={(e) => updateNested('pricing', 'base_price', e.target.value)}
        />
      </Field>

      {requiresOnline && (
        <Field label="Monto que se cobra ONLINE (Stripe)">
          <input
            type="number" min={0}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            value={form.pricing.pay_in_app}
            onChange={(e) => updateNested('pricing', 'pay_in_app', e.target.value)}
          />
          <p className="text-[11px] text-slate-500 mt-1">
            {mode === 'deposit' && 'Depósito que separa la reserva. El resto se paga en el negocio.'}
            {mode === 'full_payment' && 'Por lo general igual al precio total.'}
            {mode === 'cover_payment' && 'Cover prepagado (entrada).'}
          </p>
        </Field>
      )}

      <Field label="Pendiente a cobrar EN EL NEGOCIO">
        <input
          type="number" min={0}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          value={form.pricing.pay_at_business}
          onChange={(e) => updateNested('pricing', 'pay_at_business', e.target.value)}
        />
      </Field>

      {!requiresOnline && mode !== 'free_claim' && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">
          Modo "{MODES.find((m) => m.value === mode)?.label}" no cobra online; los campos de pago online se ignoran.
        </p>
      )}

      <div className="mt-5 pt-4 border-t border-slate-200">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Adicionales (opcional)</h4>
        <p className="text-xs text-slate-500 mb-3">
          Productos o servicios que el cliente puede agregar a su reserva. Pueden pagarse online o en el negocio.
          {mode === 'free_claim' && ' En promos gratis solo se permiten addons "en el negocio".'}
        </p>
        <div className="space-y-2">
          {addons.map((a, i) => (
            <div key={i} className="p-3 rounded-lg border border-slate-200 grid grid-cols-1 sm:grid-cols-6 gap-2">
              <input
                className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm sm:col-span-2"
                placeholder="Nombre (ej. Flores)"
                value={a.name || ''}
                onChange={(e) => setAddon(i, 'name', e.target.value)}
              />
              <input
                type="number" min={0}
                className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm"
                placeholder="Precio"
                value={a.price}
                onChange={(e) => setAddon(i, 'price', Number(e.target.value))}
              />
              <select
                value={a.pay_mode}
                onChange={(e) => setAddon(i, 'pay_mode', e.target.value)}
                className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm"
              >
                <option value="pay_in_app">Online</option>
                <option value="pay_at_business">En negocio</option>
              </select>
              <input
                type="number" min={1} max={20}
                className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm"
                placeholder="Máx"
                value={a.max_quantity}
                onChange={(e) => setAddon(i, 'max_quantity', Number(e.target.value))}
              />
              <button type="button" onClick={() => removeAddon(i)} className="text-xs text-red-600 hover:underline justify-self-end">
                Quitar
              </button>
              <input
                className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs sm:col-span-6"
                placeholder="Descripción corta (opcional)"
                value={a.description || ''}
                onChange={(e) => setAddon(i, 'description', e.target.value)}
              />
            </div>
          ))}
          <button type="button" onClick={addAddon} className="text-sm text-teal-600 hover:underline">
            + Agregar adicional
          </button>
        </div>
      </div>
    </div>
  );
}

function Step4({ form, updateNested }) {
  const c = form.affiliate_commission;
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-700 mb-3">Comisión al afiliado</h3>

      <label className="inline-flex items-center gap-2 text-sm text-slate-700 mb-3">
        <input
          type="checkbox"
          checked={!!c.enabled}
          onChange={(e) => updateNested('affiliate_commission', 'enabled', e.target.checked)}
        /> Pagar comisión a afiliados que traigan clientes
      </label>

      {c.enabled && (
        <>
          <Field label="Tipo de comisión">
            <select
              value={c.type}
              onChange={(e) => updateNested('affiliate_commission', 'type', e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            >
              {COMMISSION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </Field>

          <Field label={c.type === 'percent_of_sale' ? 'Porcentaje (%)' : 'Monto (MXN)'}>
            <input
              type="number" min={0}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              value={c.value}
              onChange={(e) => updateNested('affiliate_commission', 'value', e.target.value)}
            />
          </Field>

          {c.type === 'fixed_per_group' && (
            <Field label="Tamaño del grupo">
              <input
                type="number" min={1}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                value={c.group_size}
                onChange={(e) => updateNested('affiliate_commission', 'group_size', e.target.value)}
              />
            </Field>
          )}

          <Field label="Cuándo se gana la comisión">
            <select
              value={c.trigger}
              onChange={(e) => updateNested('affiliate_commission', 'trigger', e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            >
              {COMMISSION_TRIGGERS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Retención plataforma">
              <select
                value={c.platform_cut_type || 'fixed'}
                onChange={(e) => updateNested('affiliate_commission', 'platform_cut_type', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="fixed">Fijo (MXN)</option>
                <option value="percent">% de la comisión</option>
              </select>
            </Field>
            <Field label="Valor">
              <input
                type="number" min={0}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                value={c.platform_cut_value}
                onChange={(e) => updateNested('affiliate_commission', 'platform_cut_value', e.target.value)}
              />
            </Field>
          </div>

          <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2 mt-2">
            Si el cliente llega sin afiliado, esta comisión se asigna 100% a la plataforma.
          </p>
        </>
      )}
    </div>
  );
}

function Step5({ form, setForm, updateNested, update }) {
  const cap = form.capacity;
  const rules = form.surge_rules || [];

  function addRule() {
    setForm((f) => ({
      ...f,
      surge_rules: [...(f.surge_rules || []), {
        trigger: 'occupancy_percent',
        operator: '>=',
        value: 80,
        increase_type: 'percent',
        increase_value: 15,
      }],
    }));
  }
  function updateRule(i, key, value) {
    setForm((f) => {
      const arr = [...(f.surge_rules || [])];
      arr[i] = { ...arr[i], [key]: value };
      return { ...f, surge_rules: arr };
    });
  }
  function removeRule(i) {
    setForm((f) => ({ ...f, surge_rules: (f.surge_rules || []).filter((_, idx) => idx !== i) }));
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-700 mb-3 inline-flex items-center gap-2">
        <Users className="w-4 h-4 text-teal-600" /> Capacidad y validez
      </h3>

      <label className="inline-flex items-center gap-2 text-sm text-slate-700 mb-3">
        <input
          type="checkbox"
          checked={!!cap.enabled}
          onChange={(e) => updateNested('capacity', 'enabled', e.target.checked)}
        /> Limitar cupos
      </label>

      {cap.enabled && (
        <>
          <Field label="Cupo total">
            <input
              type="number" min={0}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              value={cap.real_capacity}
              onChange={(e) => {
                const v = e.target.value;
                updateNested('capacity', 'real_capacity', v);
                if (!cap.overbooking_enabled) updateNested('capacity', 'max_sellable_capacity', v);
              }}
            />
          </Field>

          <Field label="¿Cupo total o por día?">
            <select
              value={cap.scope || 'total'}
              onChange={(e) => updateNested('capacity', 'scope', e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="total">Cupo total (acumulado)</option>
              <option value="daily">Cupo por día (se renueva cada día)</option>
            </select>
          </Field>

          <label className="inline-flex items-center gap-2 text-sm text-slate-700 mb-2">
            <input
              type="checkbox"
              checked={!!cap.overbooking_enabled}
              onChange={(e) => updateNested('capacity', 'overbooking_enabled', e.target.checked)}
            /> Permitir overbooking
          </label>

          {cap.overbooking_enabled && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="% overbooking">
                <input
                  type="number" min={0} max={50}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={cap.overbooking_percent}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateNested('capacity', 'overbooking_percent', v);
                    const max = Math.round(Number(cap.real_capacity) * (1 + Number(v) / 100));
                    updateNested('capacity', 'max_sellable_capacity', max);
                  }}
                />
              </Field>
              <Field label="Cupo vendible máx">
                <input
                  type="number"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-50"
                  value={cap.max_sellable_capacity}
                  readOnly
                />
              </Field>
            </div>
          )}
        </>
      )}

      <Field label="Validez del ticket (días)">
        <input
          type="number" min={1} max={365}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          value={form.valid_days}
          onChange={(e) => update('valid_days', e.target.value)}
        />
      </Field>

      <Field label="Ciudad">
        <input
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          value={form.city}
          onChange={(e) => update('city', e.target.value)}
        />
      </Field>

      <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-2 mb-4">
        <input
          type="checkbox"
          checked={!!form.marketplace_visible}
          onChange={(e) => update('marketplace_visible', e.target.checked)}
        /> Mostrar también en el marketplace público
      </label>

      {/* Surge pricing — opcional */}
      <div className="mt-4 pt-4 border-t border-slate-200">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Surge pricing (opcional)
        </h4>
        <p className="text-xs text-slate-500 mb-3">
          Cuando la ocupación supere cierto %, el precio se ajusta automáticamente. Se aplica en cada nueva reserva.
        </p>
        {rules.length === 0 ? (
          <button
            type="button"
            onClick={addRule}
            disabled={!cap.enabled}
            className="text-sm text-teal-600 hover:underline disabled:text-slate-400"
            title={!cap.enabled ? 'Necesitas activar capacidad primero' : ''}
          >
            + Agregar regla de surge
          </button>
        ) : (
          <div className="space-y-2">
            {rules.map((r, i) => (
              <div key={i} className="p-3 rounded-lg border border-slate-200 grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                <Field label="Si ocupación">
                  <select
                    value={r.operator}
                    onChange={(e) => updateRule(i, 'operator', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs"
                  >
                    <option value=">=">≥</option>
                    <option value=">">{">"}</option>
                  </select>
                </Field>
                <Field label="%">
                  <input
                    type="number" min={0} max={100}
                    value={r.value}
                    onChange={(e) => updateRule(i, 'value', Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs"
                  />
                </Field>
                <Field label="Subir">
                  <select
                    value={r.increase_type}
                    onChange={(e) => updateRule(i, 'increase_type', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs"
                  >
                    <option value="percent">% del precio</option>
                    <option value="fixed">MXN fijo</option>
                  </select>
                </Field>
                <Field label="Valor">
                  <input
                    type="number" min={0}
                    value={r.increase_value}
                    onChange={(e) => updateRule(i, 'increase_value', Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs"
                  />
                </Field>
                <button
                  type="button"
                  onClick={() => removeRule(i)}
                  className="text-xs text-red-600 hover:underline justify-self-end"
                >
                  Quitar
                </button>
              </div>
            ))}
            <button type="button" onClick={addRule} className="text-sm text-teal-600 hover:underline">
              + Agregar otra regla
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Step6({ form, setForm, update }) {
  const benefitsCount = (form.customer_benefits || []).filter((b) => b.name).length;
  const c = form.affiliate_commission;
  const mode = MODES.find((m) => m.value === form.mode);
  const cat = CATEGORIES.find((c) => c.value === form.category);
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-1">Resumen y publicación</h3>

      <Field label="Título de la oferta *" required>
        <input
          required
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          value={form.title}
          onChange={(e) => update('title', e.target.value)}
          placeholder="Ej. 1 Margarita gratis al llegar"
        />
      </Field>

      <Field label="Descripción">
        <textarea
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm min-h-[80px]"
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
        />
      </Field>

      <ImagesUploader form={form} setForm={setForm} />

      <Field label="Dirección visible al cliente (opcional)">
        <input
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
          value={form.address}
          onChange={(e) => update('address', e.target.value)}
        />
      </Field>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm space-y-1.5">
        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Resumen</p>
        <SummaryRow label="Categoría" value={cat?.label} />
        <SummaryRow label="Modo" value={mode?.label} />
        <SummaryRow label="Beneficios" value={`${benefitsCount} configurado(s)`} />
        <SummaryRow label="Precio total" value={`$${Number(form.pricing.base_price || 0).toFixed(2)} MXN`} />
        <SummaryRow label="Cobro online" value={`$${Number(form.pricing.pay_in_app || 0).toFixed(2)}`} />
        <SummaryRow label="Cobro en negocio" value={`$${Number(form.pricing.pay_at_business || 0).toFixed(2)}`} />
        {c.enabled && (
          <SummaryRow
            label="Comisión afiliado"
            value={`${c.type === 'percent_of_sale' ? `${c.value}%` : `$${Number(c.value || 0).toFixed(2)}`} (${COMMISSION_TYPES.find((t) => t.value === c.type)?.label})`}
          />
        )}
        <SummaryRow label="Capacidad" value={form.capacity.enabled ? `${form.capacity.real_capacity} ${form.capacity.capacity_unit}` : 'Sin límite'} />
        <SummaryRow label="Validez ticket" value={`${form.valid_days} día(s)`} />
        <SummaryRow label="Ciudad" value={form.city} />
        <SummaryRow label="Visible en marketplace" value={form.marketplace_visible ? 'Sí' : 'No'} />
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block mb-3">
      <span className="text-xs font-semibold text-slate-700">{label}{required && ' *'}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ImagesUploader({ form, setForm }) {
  const [uploading, setUploading] = useState(false);
  const images = form.images || [];
  const MAX_IMAGES = 6;

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (images.length + files.length > MAX_IMAGES) {
      toast.error(`Máximo ${MAX_IMAGES} imágenes`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        if (!f.type.startsWith('image/')) continue;
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`"${f.name}" pesa más de 5 MB`);
          continue;
        }
        const res = await uploadProductImage(f);
        uploaded.push(res.data.image_url);
      }
      if (uploaded.length) {
        setForm((f) => ({ ...f, images: [...(f.images || []), ...uploaded] }));
        toast.success(`${uploaded.length} foto(s) subida(s)`);
      }
    } catch (err) {
      toast.error('Error subiendo imágenes');
    } finally {
      setUploading(false);
      e.target.value = ''; // permite re-elegir el mismo archivo
    }
  }

  function removeImage(idx) {
    setForm((f) => ({ ...f, images: (f.images || []).filter((_, i) => i !== idx) }));
  }

  function moveTo(idx, newIdx) {
    setForm((f) => {
      const arr = [...(f.images || [])];
      const [item] = arr.splice(idx, 1);
      arr.splice(newIdx, 0, item);
      return { ...f, images: arr };
    });
  }

  return (
    <div>
      <span className="text-xs font-semibold text-slate-700">
        Fotos de la oferta
        <span className="text-slate-400 font-normal ml-1">({images.length}/{MAX_IMAGES} — la primera es la portada)</span>
      </span>
      <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
        {images.map((url, idx) => (
          <div key={`${url}-${idx}`} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
            <img src={url} alt="" className="w-full h-full object-cover" />
            {idx === 0 && (
              <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-orange-500 text-white text-[10px] font-bold uppercase">Portada</span>
            )}
            <button
              type="button"
              onClick={() => removeImage(idx)}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 hover:bg-red-50 text-red-600 flex items-center justify-center"
              title="Quitar"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="absolute bottom-1 left-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {idx > 0 && (
                <button
                  type="button"
                  onClick={() => moveTo(idx, idx - 1)}
                  className="px-1.5 py-0.5 rounded bg-white/90 text-slate-700 text-[10px] font-bold"
                  title="Mover izquierda"
                >‹</button>
              )}
              {idx < images.length - 1 && (
                <button
                  type="button"
                  onClick={() => moveTo(idx, idx + 1)}
                  className="px-1.5 py-0.5 rounded bg-white/90 text-slate-700 text-[10px] font-bold"
                  title="Mover derecha"
                >›</button>
              )}
            </div>
          </div>
        ))}
        {images.length < MAX_IMAGES && (
          <label className={`aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-slate-400 hover:bg-slate-50 cursor-pointer ${uploading ? 'pointer-events-none opacity-60' : ''} border-slate-300`}>
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-6 h-6" />}
            <span className="text-[11px] mt-1 font-semibold">{uploading ? 'Subiendo...' : 'Agregar foto'}</span>
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFiles}
              disabled={uploading}
            />
          </label>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mt-1.5">
        Las fotos aparecen en el card de marketplace, perfil del afiliado y detalle de la oferta. La primera es la portada — arrastra con las flechas para reordenar. Hasta 5 MB cada una.
      </p>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 text-right font-medium">{value}</span>
    </div>
  );
}
